<script setup lang="ts">
const props = defineProps<{
  productRating: number;
  reviewCount: number;
  clickable?: boolean;
}>();

const { openReviewsTab, scrollToReviewsTab } = useReviewsTab();

const handleRatingClick = () => {
  if (props.clickable && props.reviewCount > 0) {
    openReviewsTab();
    // Add a longer delay to ensure the tab is switched before scrolling
    setTimeout(() => {
      scrollToReviewsTab();
    }, 300);
  }
};
</script>

<template>
  <div 
    class="flex items-center gap-2"
    :class="{ 'cursor-pointer hover:opacity-80 transition-opacity duration-200': clickable && reviewCount > 0 }"
    @click="handleRatingClick"
    @keydown.enter="handleRatingClick"
    @keydown.space="handleRatingClick"
    :role="clickable && reviewCount > 0 ? 'button' : undefined"
    :tabindex="clickable && reviewCount > 0 ? 0 : undefined"
    :aria-label="clickable && reviewCount > 0 ? `View ${reviewCount} reviews` : undefined"
  >
    <div
      class="rating-stars flex gap-x-0 sm:gap-x-[4px] text-[13px] sm:text-base text-rating-yellow relative mt-[2px] ml-1 sm:-mt-1 sm:ml-0 leading-none -mb-1"
      aria-hidden="true"
      v-if="productRating"
    >
      <div
        class="rating-stars gap-x-0 sm:gap-x-[4px] mt-0 text-[13px] sm:text-base flex text-rating-yellow relative ml-1 sm:ml-0"
        aria-hidden="true"
      >
        <div
          v-for="starIndex in 5"
          :key="starIndex"
          class="relative inline-block w-[13px] h-[13px] sm:w-[16px] sm:h-[16px]"
        >
          <!-- Gray background star -->
          <i class="icon-star-sharp text-gray-300 absolute inset-0"></i>
          <!-- Yellow foreground star (full or partial) -->
          <i
            v-if="starIndex <= Math.floor(productRating)"
            class="icon-star-sharp text-rating-yellow absolute inset-0"
          ></i>
          <!-- Half star overlay for decimal values -->
          <i
            v-if="
              starIndex === Math.ceil(productRating) &&
              productRating % 1 !== 0
            "
            class="icon-star-sharp text-rating-yellow absolute inset-0"
            :style="{
              clipPath: `polygon(0 0, ${(productRating % 1) * 100}% 0, ${(productRating % 1) * 100}% 100%, 0 100%)`,
            }"
          ></i>
        </div>
      </div>
    </div>
    <span class="text-gray-600 leading-none" v-if="reviewCount > 0"
      >({{ reviewCount }})</span
    >
  </div>
</template>
